/** @jsxImportSource @emotion/react */
import {css, SerializedStyles} from '@emotion/react';
import styled from '@emotion/styled';
import {FADE_IN_ANIMATION} from '../styles/animations';
import {COOL_GREY, COOLER_GREY, LIGHT_GREY, RED_CREEK, WHITE} from '../styles/colors';
import {DROP_SHADOW, MediumText, UNSELECTABLE_TEXT} from '../styles/styles';
import {GuestBasics} from '../guest/guestBasics';
import ImmutableMap from '../language/ImmutableMap';
import {EntitlementType} from '../entitlementTypes/entitlementType';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import {useCallback, useEffect, useState} from 'react';
import {IAppState} from '../core/redux/IAppState';
import {getHousehold} from './module';
import {getError, getErrors, getLoading} from '../core/redux/api/module';
import {ACTION_TYPE} from '../core/redux/actionType';
import {isNilOrEmpty} from '../language/module';
import { Collapse } from 'react-collapse';
import {householdAction} from './household/householdAction';
import {
  Blank,
  CARD_GRID_PADDING, CARD_HEIGHT,
  CARD_MARGIN,
} from '../guest/guestCard/guestCardStyles';
import * as React from 'react';
import * as R from 'ramda';
import {getGuestDetailsAction} from '../guest/guestDetailsAction';
import HorizontalGuestDetails from './household/HorizontalGuestDetails';
import {onIe} from '../language/browser';
import {Loader} from '../forms/elements/loader/Loader';
import GuestCard from '../guest/guestCard/GuestCard';
import {ApiError} from '../core/redux/api/apiError';
import TabSelectDiv from '../layout/TabSelectDiv';
import arrowIcon from '../assets/icons/downArrow.svg';
import { getResource } from '../language/resources/getResource';
import { GuestDetails } from '../guest/guestDetails';
import { getPhotoData } from '../guest/guestPhoto/module';


const regularArrow  = css`transform: rotate(0deg);`;
const invertedArrow = css`transform: rotate(180deg);`;

const rootStyle = css`
  background-color: ${WHITE};
  border: solid ${COOL_GREY} 1px;
  ${DROP_SHADOW};
`;

const expandArrow = (isExpanded) => css`
  width: 30px;
  height: 30px;
  ${isExpanded ? invertedArrow : regularArrow};
  ${UNSELECTABLE_TEXT};
`;

const hideOrViewGuests = css`
  background-color: ${COOL_GREY};
  display: flex;
  flex-direction: row;
  justify-content: center;
  cursor: pointer;

  &:hover {
    background-color: ${COOLER_GREY}
  }
`;

const loaderCss = css`
  justify-self: center;
  align-self: center;
  {/* Fewer re-renders using the height of the card as the loader height */}
  margin-top: ${(CARD_HEIGHT - 100) / 2 + CARD_MARGIN}px;
  margin-bottom: ${(CARD_HEIGHT - 100) / 2 + CARD_MARGIN}px;
  width: 100px;
  height: 100px;
`;

const householdMembers = css`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: ${onIe() ? 'flex-start' : 'space-around'};
  background-color: ${LIGHT_GREY};
  padding: ${CARD_GRID_PADDING}px;
`;

const ErrorText = styled('span')`
  ${MediumText};
  color: ${RED_CREEK};
`;

type Props = {
  guest: GuestBasics;
  resultCount: number;
  entitlementTypes: ImmutableMap<EntitlementType> | undefined;
  isLoadingEntitlementTypes: boolean;
  rootCss?: SerializedStyles;
}

export const ExpandingGuestSearchResult = (props: Props) => {
  const dispatch = useDispatch();
  const [isExpanded, setIsExpanded] = useState(false);
  const [cardsperRow] = useState(0);
  const household = useSelector((state: IAppState) => getHousehold(state, props.guest.masterGuestId) || [], shallowEqual);
  const householdGuests = useSelector((state: IAppState): GuestDetails[] => {
  const household = getHousehold(state, props.guest.masterGuestId) || [];
    return household
      .map(guest => state.guests.get(guest.masterGuestId) as GuestDetails | undefined)
      .filter((guest): guest is GuestDetails => !!guest);
  }, shallowEqual);
  const householdLoading = useSelector((state: IAppState) => !!getLoading(state, props.guest.masterGuestId, ACTION_TYPE.HOUSEHOLD_REQUEST));
  const householdLoadError = useSelector((state: IAppState) => getError(state, props.guest.masterGuestId, ACTION_TYPE.HOUSEHOLD_REQUEST));
  const guestDetailsErrors = useSelector((state: IAppState): ApiError[] => {
    const household = getHousehold(state, props.guest.masterGuestId) || [];
    return !isNilOrEmpty(household) ? getErrors(
      state,
      household.map(m => m.masterGuestId),
      ACTION_TYPE.GUEST_DETAILS_REQUEST,
    ) : [];
  }, shallowEqual);
  // The guest card's photo, so the summary row picks up a newly uploaded photo instead of keeping the one captured
  // when the search ran.
  const photoData = useSelector((state: IAppState) =>
    getPhotoData(state.guests.get(props.guest.masterGuestId) as GuestDetails | undefined));
  const resources = useSelector((state: IAppState) => state.locale);
  const dateFormat = useSelector((state: IAppState) => state.defaultDateFormat);

  const loadHousehold = useCallback(() => {
    if (isNilOrEmpty(household)) {
      dispatch(householdAction(props.guest.masterGuestId));
    }
  },[dispatch, household, props.guest.masterGuestId]);

  const onSelect = () => {
    setIsExpanded(!isExpanded);
    loadHousehold();
  }

  const countPlaceholders = (
    guestCount: number,
    cardsPerRow: number,
  ): number => Math.max(cardsPerRow - guestCount, cardsPerRow % guestCount);

  const makeGuestCardPlaceholders = (
    numLayout: number,
  ): React.ReactNode[] => R.range(1, numLayout + 1)
    .map(i => <Blank key={i} css={css`margin: 0 ${CARD_MARGIN}px;`} />);

  useEffect(()=> {
    if (props.resultCount === 1) {
      setIsExpanded(true);
      loadHousehold();
    } else {
      loadHousehold();
    }
  }, [loadHousehold, props.resultCount])

  useEffect(() => {
    const canFetchGuests = household.length > 0 && isNilOrEmpty(householdGuests);
    if (isExpanded && canFetchGuests) {
      household.map(member => dispatch(getGuestDetailsAction(member.masterGuestId)));
    }
  }, [isExpanded, householdLoading, loadHousehold, dispatch, household, householdGuests])

  const hasHouseholdError = !isNilOrEmpty(householdLoadError);
  const hasHousehold      = !isNilOrEmpty(household);

  return (
    <div
      css={css`
            ${FADE_IN_ANIMATION};
            ${rootStyle};
            ${props.rootCss};
          `}
      id={`search-result-${isExpanded ? 'expanded' : 'collapsed'}-${props.guest.masterGuestId}`}
    >
      {/* Household's Guest's Summary details */}
      <HorizontalGuestDetails
        resources={resources}
        dateFormat={dateFormat}
        guest={props.guest}
        isExpanded={isExpanded}
        householdSize={(household || []).length}
        photoData={photoData}
      />

      {/* Household Members */}
      <Collapse
        isOpened={isExpanded}
        springConfig={{ stiffness: 300, damping: 40, precision: .1 }}
      >
        {/* IE can't redistribute extra space to dynamic children -- we have to align cards to the left */}
        <div
          css={householdMembers}
        >
          {
            hasHouseholdError && <ErrorText>
              {getResource(resources, 'GUEST_SEARCH_RESULT_UNEXPECTED_ERROR')}
            </ErrorText>
          }
          {
            // No error AND no household -- we loading
            !hasHouseholdError
            && !hasHousehold
            && <Loader rootCss={loaderCss}/>
          }
          {
            // No error AND household -- show household members
            !hasHouseholdError && hasHousehold && household.map(
              member => (
                <GuestCard
                  key={member.masterGuestId}
                  rootCss={css`margin: ${CARD_MARGIN}px; ${DROP_SHADOW};`}
                  guest={R.find(
                    (guest: GuestDetails) => guest?.masterGuestId === member.masterGuestId,
                    householdGuests || [],
                  )}
                  entitlementTypes={props.entitlementTypes}
                  loadingGuestDetails={!R.any(
                    (guest: GuestDetails) => guest?.masterGuestId === member.masterGuestId,
                    householdGuests || [],
                  )}
                  guestDetailsError={
                    R.find(
                      (error: ApiError) => error.requestId === member.masterGuestId,
                      guestDetailsErrors || [],
                    )?.errorMsg
                  }
                  resources={resources}
                />
              ),
            )
          }
          { // Fill in last row of guest cards with dummies to maintain alignment
            hasHousehold && makeGuestCardPlaceholders(
              countPlaceholders(household.length, cardsperRow),
            )
          }
        </div>
      </Collapse>

      {/* Expand/Contract button */}
      <TabSelectDiv onSelect={onSelect}>
        <div
          title={isExpanded ? getResource(resources, 'GUEST_SEARCH_RESULT_HIDE_GUESTS') : getResource(resources, 'GUEST_SEARCH_RESULT_VIEW_GUESTS')}
          css={hideOrViewGuests}
        >
          <img
            alt='Toggle Expand Guest Results'
            src={arrowIcon}
            css={expandArrow(isExpanded)}
          />
        </div>
      </TabSelectDiv>
    </div>
  );
};
